import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import BackButton from '../../components/ui/BackButton';
import { dailyTaskService } from '../../services/taskService';
import { siteService } from '../../services/siteService';
import { workerService } from '../../services/workerService';
import { assignmentService } from '../../services/assignmentService';
import { taskCompletionPhotosService } from '../../services/taskCompletionPhotosService';
import { companyService } from '../../services/companyService';
import { toolboxTalksService } from '../../services/toolboxTalksService';
import { useAuth } from '../../contexts/AuthContext';
import { TASK_CATEGORIES } from '../../data/taskData';
import { useLanguage } from '../../contexts/LanguageContext';
import MobilePhotoInput from '../../components/ui/MobilePhotoInput';
import NotesFeed from '../../components/ui/NotesFeed';
import TaskDetailModal from '../../components/TaskDetailModal';
import SiteReadOnlyBanner from '../../components/SiteReadOnlyBanner';
import useSiteReadOnly from '../../hooks/useSiteReadOnly';
import { supabase } from '../../lib/supabase';

const CATEGORY_COLORS = {
  Safety: 'bg-red-100 text-red-700 border-red-200',
  Quality: 'bg-blue-100 text-blue-700 border-blue-200',
  Operations: 'bg-amber-100 text-amber-700 border-amber-200',
  Documentation: 'bg-purple-100 text-purple-700 border-purple-200',
};

const CATEGORY_DOT = {
  Safety: 'bg-red-500',
  Quality: 'bg-blue-500',
  Operations: 'bg-amber-500',
  Documentation: 'bg-purple-500',
};

const ProgressBar = ({ value, color = 'bg-accent' }) => (
  <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
    <div className={`h-full rounded-full transition-all duration-500 ${color}`} style={{ width: `${Math.min(value, 100)}%` }} />
  </div>
);

const CompleteModal = ({ task, onClose, onComplete, requirePhotoEvidence }) => {
  const [notes, setNotes] = useState('');
  const [photoAdded, setPhotoAdded] = useState(false);
  const [status, setStatus] = useState('completed');
  const { user, userProfile } = useAuth();
  const { t } = useLanguage();

  // "Work carried out by" — free-text with autocomplete over same-company
  // user_profiles. Optional: if blank, work is assumed to have been done by
  // the logger. Anyone can type any name (may not have a SiteProof login).
  const [workCarriedOutBy, setWorkCarriedOutBy] = useState('');
  const [nameSuggestions, setNameSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    const q = workCarriedOutBy?.trim();
    if (!q || q?.length < 2 || !userProfile?.company_id) {
      setNameSuggestions([]);
      return;
    }
    let cancelled = false;
    const timeout = setTimeout(async () => {
      // jyyb8AfJ — exclude admin-deactivated users from the "work carried
      // out by" typeahead so admins can't autofill a deactivated name.
      const { data } = await supabase
        ?.from('user_profiles')
        ?.select('id, full_name')
        ?.eq('company_id', userProfile?.company_id)
        ?.ilike('full_name', `%${q}%`)
        ?.is('deactivated_at', null)
        ?.order('full_name')
        ?.limit(5);
      if (!cancelled) setNameSuggestions(data || []);
    }, 200);
    return () => { cancelled = true; clearTimeout(timeout); };
  }, [workCarriedOutBy, userProfile?.company_id]);

  // Photo evidence state
  const [saved, setSaved] = useState(false);
  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [photoRequiredError, setPhotoRequiredError] = useState('');

  const handleSave = async () => {
    if (status === 'completed') {
      const minRequired = task?.photo_required ? Math.max(task?.min_photos || 1, 1) : 0;
      const photoCount = uploadedPhotos?.length || 0;

      // Per-task photo requirement takes priority
      if (minRequired > 0 && photoCount < minRequired) {
        setPhotoRequiredError(
          `This task requires at least ${minRequired} photo${minRequired > 1 ? 's' : ''} to complete. You've attached ${photoCount}.`
        );
        return;
      }

      // Company-wide photo evidence requirement (fallback for tasks without per-task requirement)
      if (requirePhotoEvidence && !task?.photo_required && photoCount === 0) {
        setPhotoRequiredError('Photo evidence required — please upload at least one photo before completing this task');
        return;
      }
    }

    // Blocked tasks must include a note explaining why — otherwise admins
    // receive an actionless "task blocked" alert with no context.
    if (status === 'blocked' && !notes?.trim()) {
      setPhotoRequiredError('Please explain why this task is blocked — a note is required.');
      return;
    }

    setPhotoRequiredError('');
    await onComplete(task?.id, status, notes, workCarriedOutBy);
    setSaved(true);
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e?.target?.files || []);
    if (!files?.length) return;
    await uploadFiles(files);
    e.target.value = '';
  };

  const uploadFiles = async (files) => {
    const MAX_SIZE = 26214400; // 25MB — keep in sync with task-photos bucket file_size_limit
    setUploading(true);
    setUploadError('');
    for (const file of files) {
      if (file?.size > MAX_SIZE) {
        setUploadError(`This photo is too large (${Math.round(file.size / 1024 / 1024)}MB). Maximum size is 25MB.`);
        continue;
      }
      const { data, error } = await taskCompletionPhotosService?.uploadCompletionPhoto(
        task?.id,
        file,
        user?.id
      );
      if (error) {
        const friendly = error?.code === 'PGRST301' || error?.code === '42501'
          ? `Failed to upload ${file?.name}: permission denied — ${error?.message}`
          : `Failed to upload ${file?.name}: ${error?.message || 'Unknown error'}`;
        setUploadError(friendly);
      } else if (data) {
        const objectUrl = URL.createObjectURL(file);
        setUploadedPhotos(prev => [...prev, { id: data?.id, name: file?.name, previewUrl: objectUrl }]);
      }
    }
    setUploading(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-4">
      <div className="bg-card rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <h3 className="font-semibold text-foreground">{saved ? t('taskCompleted') : t('completeTask')}</h3>
          <button onClick={onClose} className="w-10 h-10 rounded-lg hover:bg-muted flex items-center justify-center"><Icon name="X" size={18} /></button>
        </div>

        {!saved ? (
          <>
            <div className="px-5 py-4 space-y-4">
              <div className="bg-muted/50 rounded-xl p-3">
                <p className="text-sm font-semibold text-foreground">{task?.title}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{task?.category} · {task?.required ? t('required') : t('optional')}</p>
                {task?.description && (
                  <p className="text-sm text-foreground mt-2 whitespace-pre-wrap">
                    {task?.description}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">{t('status')}</label>
                <div className="flex gap-2">
                  {[{ v: 'completed', label: t('completed'), color: 'bg-emerald-100 text-emerald-700 border-emerald-300' }, { v: 'blocked', label: t('blocked'), color: 'bg-red-100 text-red-700 border-red-300' }]?.map(opt => (
                    <button key={opt?.v} onClick={() => setStatus(opt?.v)} className={`flex-1 min-h-[48px] py-3 rounded-lg text-base font-semibold border transition-all ${status === opt?.v ? opt?.color : 'bg-background text-muted-foreground border-border'}`}>{opt?.label}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">
                  {status === 'blocked' ? (
                    <>Notes <span className="text-red-500 normal-case">(required)</span></>
                  ) : (
                    t('notesOptional')
                  )}
                </label>
                <textarea
                  value={notes}
                  onChange={e => setNotes(e?.target?.value)}
                  placeholder={status === 'blocked' ? 'Explain why this task is blocked…' : t('addTaskNotes')}
                  rows={3}
                  className={`w-full rounded-lg border bg-background px-3 py-3 text-base text-foreground focus:outline-none focus:ring-2 focus:ring-accent resize-none ${
                    status === 'blocked' && !notes?.trim() ? 'border-red-500' : 'border-border'
                  }`}
                />
                {status === 'blocked' && !notes?.trim() && (
                  <p className="mt-1.5 text-xs text-red-600 font-medium">
                    Please explain why this task is blocked — a note is required.
                  </p>
                )}
              </div>
              {status === 'completed' && (
                <div className="relative">
                  <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">
                    Work carried out by <span className="text-muted-foreground font-normal normal-case">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={workCarriedOutBy}
                    onChange={e => { setWorkCarriedOutBy(e?.target?.value); setShowSuggestions(true); }}
                    onFocus={() => setShowSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                    placeholder="Leave blank if you did the work"
                    className="w-full rounded-lg border border-border bg-background px-3 py-3 text-base text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                    autoComplete="off"
                  />
                  {showSuggestions && nameSuggestions?.length > 0 && (
                    <div className="absolute left-0 right-0 top-full mt-1 bg-popover border border-border rounded-lg shadow-elevation-3 z-dropdown max-h-48 overflow-y-auto">
                      {nameSuggestions?.map(p => (
                        <button
                          key={p?.id}
                          type="button"
                          onMouseDown={e => e?.preventDefault()}
                          onClick={() => { setWorkCarriedOutBy(p?.full_name || ''); setShowSuggestions(false); }}
                          className="w-full text-left px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors"
                        >
                          {p?.full_name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {/* Photo requirement banner */}
              {(task?.photo_required || requirePhotoEvidence) && status === 'completed' && (
                <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2 flex items-start gap-2">
                  <Icon name="Camera" size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-700 dark:text-amber-400 font-medium">
                    {task?.photo_required
                      ? `This task requires at least ${task?.min_photos || 1} photo${(task?.min_photos || 1) > 1 ? 's' : ''} to complete.`
                      : 'Photo evidence required — upload at least one photo before completing.'}
                  </p>
                </div>
              )}
              {/* Photo upload zone — shown pre-save when photo_required (per-task) OR requirePhotoEvidence (company) */}
              {(task?.photo_required || requirePhotoEvidence) && status === 'completed' && (
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">
                    Photo Evidence <span className="text-red-500">*</span>
                  </label>
                  {uploadedPhotos?.length > 0 && (
                    <div className="grid grid-cols-3 gap-2 mb-2">
                      {uploadedPhotos?.map(photo => (
                        <div key={photo?.id} className="relative aspect-square rounded-lg overflow-hidden border border-border bg-muted">
                          <img src={photo?.previewUrl} alt={photo?.name} className="w-full h-full object-cover" />
                        </div>
                      ))}
                    </div>
                  )}
                  {uploadError && (
                    <div className="mb-2 p-2 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                      <Icon name="AlertCircle" size={14} className="text-red-500 flex-shrink-0" />
                      <p className="text-xs text-red-700">{uploadError}</p>
                    </div>
                  )}
                  <MobilePhotoInput
                    onFilesSelected={uploadFiles}
                    uploading={uploading}
                    multiple
                    compact={true}
                    label="Upload Photos"
                    hint="Select one or more photos as evidence"
                  />
                  {uploadedPhotos?.length > 0 && (
                    <p className="text-xs text-emerald-600 font-medium mt-1.5 flex items-center gap-1">
                      <Icon name="CheckCircle2" size={12} />
                      {uploadedPhotos?.length} photo{uploadedPhotos?.length !== 1 ? 's' : ''} ready
                    </p>
                  )}
                </div>
              )}
              {photoRequiredError && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 flex items-start gap-2">
                  <Icon name="AlertCircle" size={14} className="text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-700 font-medium">{photoRequiredError}</p>
                </div>
              )}
            </div>
            <div className="px-5 py-4 border-t border-border flex gap-3">
              <Button variant="outline" size="lg" onClick={onClose} className="flex-1">{t('cancel')}</Button>
              <Button
                variant="primary"
                size="lg"
                onClick={handleSave}
                disabled={status === 'blocked' && !notes?.trim()}
                className="flex-1"
              >
                {t('save')}
              </Button>
            </div>
          </>
        ) : (
          <div className="px-5 py-4 space-y-4">
            {/* Success confirmation */}
            <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-xl p-3">
              <Icon name="CheckCircle2" size={20} className="text-emerald-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-emerald-700">{t('taskMarkedAs')} {status}</p>
                <p className="text-xs text-emerald-600 mt-0.5">{task?.title}</p>
              </div>
            </div>

            {/* Photo evidence upload */}
            <div>
              <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">{t('uploadPhotoEvidence')} <span className="text-muted-foreground font-normal normal-case">({t('optional')})</span></label>

              {uploadedPhotos?.length > 0 && (
                <div className="grid grid-cols-3 gap-2 mb-3">
                  {uploadedPhotos?.map(photo => (
                    <div key={photo?.id} className="relative aspect-square rounded-lg overflow-hidden border border-border bg-muted">
                      <img src={photo?.previewUrl} alt={photo?.name} className="w-full h-full object-cover" />
                      <div className="absolute bottom-0 left-0 right-0 bg-black/50 px-1.5 py-1">
                        <p className="text-white text-[10px] truncate">{photo?.name}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {uploadError && (
                <div className="mb-2 p-2 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                  <Icon name="AlertCircle" size={14} className="text-red-500 flex-shrink-0" />
                  <p className="text-xs text-red-700">{uploadError}</p>
                </div>
              )}

              <MobilePhotoInput
                onFilesSelected={uploadFiles}
                uploading={uploading}
                multiple
                compact={true}
                label="Upload Photos"
                hint="Select one or more photos as evidence"
              />

              {uploadedPhotos?.length > 0 && (
                <p className="text-xs text-emerald-600 font-medium mt-2 flex items-center gap-1">
                  <Icon name="CheckCircle2" size={12} />
                  {uploadedPhotos?.length} {uploadedPhotos?.length !== 1 ? t('photosUploaded') : t('photoUploaded')}
                </p>
              )}
            </div>

            <div className="pt-2">
              <Button variant="primary" size="lg" onClick={onClose} className="w-full">{t('done')}</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Task Notes Modal — opens NotesFeed for a specific task
const TaskNotesModal = ({ task, companyId, onClose }) => {
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-4">
      <div className="bg-card rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-foreground">Task Notes</h3>
            <p className="text-xs text-muted-foreground mt-0.5 truncate max-w-[280px]">{task?.title}</p>
          </div>
          <button onClick={onClose} className="w-10 h-10 rounded-lg hover:bg-muted flex items-center justify-center">
            <Icon name="X" size={18} />
          </button>
        </div>
        <div className="p-4">
          {companyId ? (
            <NotesFeed
              entityType="task"
              entityId={task?.id}
              companyId={companyId}
              title="Task Notes"
            />
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">Loading…</p>
          )}
        </div>
      </div>
    </div>
  );
};

const TaskCompletionWorkflow = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { userProfile, user } = useAuth();
  const { t } = useLanguage();

  const filterWorker = searchParams?.get('worker');
  const filterSite = searchParams?.get('site');
  const filterStatus = searchParams?.get('status');

  const [tasks, setTasks] = useState([]);
  const [sites, setSites] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedWorker, setSelectedWorker] = useState(filterWorker || 'all');
  const [selectedSite, setSelectedSite] = useState(filterSite || 'all');
  const [selectedStatus, setSelectedStatus] = useState(filterStatus || 'all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedDate, setSelectedDate] = useState(new Date()?.toISOString()?.split('T')?.[0]);
  const [completingTask, setCompletingTask] = useState(null);
  const [showCompleted, setShowCompleted] = useState(false);
  const [notesTask, setNotesTask] = useState(null);
  const [detailTask, setDetailTask] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [assignedSite, setAssignedSite] = useState(null);
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [requirePhotoEvidence, setRequirePhotoEvidence] = useState(false);
  const [toolboxTalkBlocked, setToolboxTalkBlocked] = useState(false);
  const [pendingToolboxCount, setPendingToolboxCount] = useState(0);

  const isWorker = userProfile?.role === 'worker';

  const gatedSiteId = selectedSite && selectedSite !== 'all' ? selectedSite : null;
  const { status: gatedSiteStatus, isReadOnly: siteIsReadOnly } = useSiteReadOnly(gatedSiteId);

  // Fetch company photo evidence setting for worker role
  useEffect(() => {
    if (!isWorker || !userProfile?.company_id) return;
    const fetchSetting = async () => {
      const { data } = await companyService?.getUserCompany();
      if (data?.require_photo_evidence) {
        setRequirePhotoEvidence(true);
      }
      // Check toolbox talk sign-off gate
      if (data?.require_toolbox_talk_signoff && user?.id) {
        const talksResult = await toolboxTalksService?.getTalksForWorker(user?.id);
        if (talksResult?.error) {
          // Gate-check failure — log so it's diagnosable, but don't block
          // task completion on a read failure. Falls open by design.
          console.error('[TaskCompletionWorkflow] toolbox gate check failed:', { code: talksResult?.error?.code, message: talksResult?.error?.message });
        } else if (talksResult?.data?.length > 0) {
          const statusChecks = await Promise.all(
            talksResult?.data?.map(a =>
              toolboxTalksService?.getWorkerConfirmationStatus(a?.talk_id, user?.id)
            )
          );
          const pending = statusChecks?.filter(r => !r?.data)?.length;
          if (pending > 0) {
            setPendingToolboxCount(pending);
            setToolboxTalkBlocked(true);
          }
        }
      }
    };
    fetchSetting();
  }, [isWorker, userProfile?.company_id, user?.id]);

  // For workers: auto-detect or create today's site assignment
  useEffect(() => {
    if (!isWorker || !user?.id || !userProfile?.company_id) return;
    const resolveAssignment = async () => {
      setAssignmentLoading(true);
      const { data, error: assignErr } = await assignmentService?.ensureTodayAssignment(
        user?.id,
        userProfile?.company_id
      );
      if (!assignErr && data?.site_id) {
        setAssignedSite(data);
        setSelectedSite(data?.site_id);
      }
      setAssignmentLoading(false);
    };
    resolveAssignment();
  }, [isWorker, user?.id, userProfile?.company_id]);

  useEffect(() => {
    loadData();
    loadCompanyId();
  }, [user, selectedDate]);

  const loadCompanyId = async () => {
    const result = await companyService?.getUserCompany();
    if (result?.data?.id) setCompanyId(result?.data?.id);
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      if (isWorker) {
        // Workers: fetch only tasks assigned to this worker via assigned_to_user_id
        const tasksResult = await dailyTaskService?.getForWorkerToday(user?.id, selectedDate);
        if (tasksResult?.error) throw new Error(tasksResult?.error);
        setTasks(tasksResult?.data || []);
        // Keep sites/workers empty for worker role — not needed
      } else {
        const [tasksResult, sitesResult, workersResult] = await Promise.all([
          dailyTaskService?.getByDateAndSite(selectedDate, selectedSite !== 'all' ? selectedSite : null),
          siteService?.getAllSites(),
          workerService?.getAllWorkers(),
        ]);
        if (tasksResult?.error) throw new Error(tasksResult?.error);
        setTasks(tasksResult?.data || []);
        setSites(sitesResult?.data || []);
        setWorkers((workersResult?.data || [])?.filter(w => w?.role === 'worker'));
      }
    } catch (err) {
      console.error('[TaskCompletionWorkflow] load tasks failed:', err);
      setError('Failed to load tasks. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const filteredTasks = useMemo(() => {
    return tasks?.filter(t => {
      if (selectedWorker !== 'all' && t?.assigned_to_user_id !== selectedWorker) return false;
      // Workers don't have a site picker in this UI — selectedSite gets auto-set to
      // their resolved assignment for today, which would hide tasks assigned to them
      // on other sites. Only apply the site filter for non-worker roles.
      if (!isWorker && selectedSite !== 'all' && t?.site_id !== selectedSite) return false;
      if (selectedCategory !== 'all' && t?.category !== selectedCategory) return false;
      if (selectedStatus === 'overdue') {
        if (t?.status === 'completed') return false;
        if (!t?.due_time) return false;
        const now = new Date();
        const [h, m] = t?.due_time?.split(':')?.map(Number);
        const due = new Date(); due?.setHours(h, m, 0, 0);
        return now > due;
      }
      if (selectedStatus !== 'all' && t?.status !== selectedStatus) return false;
      return true;
    });
  }, [tasks, selectedWorker, selectedSite, selectedStatus, selectedCategory]);

  const activeTasks = filteredTasks?.filter(t => t?.status !== 'completed');
  const completedTasks = filteredTasks?.filter(t => t?.status === 'completed');
  const overdueTasks = dailyTaskService?.getOverdueTasks(filteredTasks);
  const completionRate = filteredTasks?.length > 0 ? Math.round((completedTasks?.length / filteredTasks?.length) * 100) : 0;

  const handleComplete = async (taskId, status, notes, workCarriedOutBy) => {
    if (siteIsReadOnly) {
      setError('This site is read-only. Task completions cannot be saved.');
      setCompletingTask(null);
      return;
    }
    const workedBy = workCarriedOutBy?.trim() || null;
    // Optimistic update
    setTasks(prev => prev?.map(t => t?.id === taskId ? {
      ...t, status, notes,
      completed_at: status === 'completed' ? new Date()?.toISOString() : null,
      completed_by: status === 'completed' ? user?.id : null,
      work_carried_out_by: status === 'completed' ? workedBy : null,
    } : t));
    setCompletingTask(null);
    // Persist to DB
    await dailyTaskService?.updateStatus(taskId, status, notes, user?.id, workedBy);
  };

  const getBarColor = (r) => r >= 80 ? 'bg-emerald-500' : r >= 50 ? 'bg-amber-500' : 'bg-red-500';

  const isOverdue = (task) => {
    if (task?.status !== 'pending') return false;
    if (!task?.due_time) return false;
    const now = new Date();
    const [h, m] = task?.due_time?.split(':')?.map(Number);
    const due = new Date(); due?.setHours(h, m, 0, 0);
    return now > due;
  };

  return (
    <>
      <Helmet><title>My Tasks — SiteProof</title></Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16">
          <div className="bg-card border-b border-border">
            <div className="max-w-2xl lg:max-w-screen-xl mx-auto px-4 lg:px-8">
              <div className="flex items-center justify-between py-5">
                <div>
                  <div className="mb-2">
                    <BackButton fallback="/worker-today" />
                  </div>
                  <h1 className="text-2xl font-bold text-foreground font-heading">{isWorker ? t('myTasks') : t('taskOverview')}</h1>
                  <p className="text-sm text-muted-foreground mt-0.5">{selectedDate}</p>
                  {isWorker && assignedSite?.site_name && (
                    <div className="flex items-center gap-1.5 mt-1">
                      <Icon name="MapPin" size={13} className="text-accent" />
                      <span className="text-xs font-medium text-accent">{assignedSite?.site_name}</span>
                    </div>
                  )}
                </div>
                {!isWorker && (
                  <div className="flex flex-wrap items-center gap-2">
                    <Button variant="outline" size="sm" iconName="BarChart2" iconPosition="left" onClick={() => navigate('/task-analytics-dashboard')}>{t('analytics')}</Button>
                    <Button variant="primary" size="sm" iconName="Plus" iconPosition="left" onClick={() => navigate('/task-planner')}>{t('planTasks')}</Button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="max-w-2xl lg:max-w-screen-xl mx-auto px-3 sm:px-4 lg:px-8 py-5">
            <SiteReadOnlyBanner status={gatedSiteStatus} siteId={gatedSiteId} className="mb-4" />

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                <Icon name="AlertCircle" size={16} className="text-red-500" />
                <p className="text-sm text-red-700">{error}</p>
                <button onClick={loadData} className="ml-auto text-xs font-semibold text-red-700 hover:underline">{t('retry')}</button>
              </div>
            )}

            {/* Toolbox talk gate for workers */}
            {isWorker && toolboxTalkBlocked && (
              <div className="mb-5 flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <Icon name="AlertTriangle" size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-amber-800">Toolbox talk sign-off required</p>
                  <p className="text-xs text-amber-700 mt-1">
                    You have {pendingToolboxCount} pending toolbox talk{pendingToolboxCount !== 1 ? 's' : ''} to sign off before you can start tasks. Please complete them first.
                  </p>
                  <button
                    onClick={() => navigate('/toolbox-talks-worker-view')}
                    className="mt-2 text-xs font-semibold text-amber-800 underline"
                  >
                    Go to Toolbox Talks →
                  </button>
                </div>
              </div>
            )}

            {/* Filters */}
            <div className="bg-card rounded-xl border border-border shadow-elevation-2 p-4 mb-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1.5">{t('date')}</label>
                  <input type="date" value={selectedDate} onChange={e => setSelectedDate(e?.target?.value)} className="w-full rounded-lg border border-border bg-background px-3 py-3 text-base text-foreground focus:outline-none focus:ring-2 focus:ring-accent min-h-[48px]" />
                </div>
                {!isWorker && (
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground mb-1.5">{t('worker')}</label>
                    <select value={selectedWorker} onChange={e => setSelectedWorker(e?.target?.value)} className="w-full rounded-lg border border-border bg-background px-3 py-3 text-base text-foreground focus:outline-none focus:ring-2 focus:ring-accent min-h-[48px]">
                      <option value="all">{t('allWorkers')}</option>
                      {workers?.map(w => <option key={w?.id} value={w?.id}>{w?.full_name}</option>)}
                    </select>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1.5">{t('site')}</label>
                  {isWorker ? (
                    <div className="w-full rounded-lg border border-border bg-muted/40 px-3 py-3 text-base text-foreground min-h-[48px] flex items-center">
                      {assignmentLoading ? t('loading') : (assignedSite?.site_name || t('noSiteAssigned'))}
                    </div>
                  ) : (
                    <select value={selectedSite} onChange={e => setSelectedSite(e?.target?.value)} className="w-full rounded-lg border border-border bg-background px-3 py-3 text-base text-foreground focus:outline-none focus:ring-2 focus:ring-accent min-h-[48px]">
                      <option value="all">{t('allSites')}</option>
                      {sites?.map(s => <option key={s?.id} value={s?.id}>{s?.site_name}</option>)}
                    </select>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1.5">{t('status')}</label>
                  <select value={selectedStatus} onChange={e => setSelectedStatus(e?.target?.value)} className="w-full rounded-lg border border-border bg-background px-3 py-3 text-base text-foreground focus:outline-none focus:ring-2 focus:ring-accent min-h-[48px]">
                    <option value="all">{t('all')}</option>
                    <option value="pending">{t('pending')}</option>
                    <option value="completed">{t('completed')}</option>
                    <option value="blocked">{t('blocked')}</option>
                    <option value="overdue">{t('overdue')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1.5">{t('category')}</label>
                  <select value={selectedCategory} onChange={e => setSelectedCategory(e?.target?.value)} className="w-full rounded-lg border border-border bg-background px-3 py-3 text-base text-foreground focus:outline-none focus:ring-2 focus:ring-accent min-h-[48px]">
                    <option value="all">{t('all')}</option>
                    {TASK_CATEGORIES?.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-accent" />
              </div>
            ) : (
              <>
                {/* Stats row */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                  {[
                    { label: t('total'), value: filteredTasks?.length, color: 'text-foreground', bg: 'bg-card' },
                    { label: t('completed'), value: completedTasks?.length, color: 'text-emerald-600', bg: 'bg-emerald-50' },
                    { label: t('overdue'), value: overdueTasks?.length, color: 'text-orange-600', bg: 'bg-orange-50' },
                    { label: t('rate'), value: `${completionRate}%`, color: completionRate >= 80 ? 'text-emerald-600' : completionRate >= 50 ? 'text-amber-600' : 'text-red-600', bg: 'bg-accent/5' },
                  ]?.map(stat => (
                    <div key={stat?.label} className={`${stat?.bg} rounded-xl p-4 border border-border shadow-elevation-1`}>
                      <p className={`text-2xl font-bold ${stat?.color}`}>{stat?.value}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{stat?.label}</p>
                    </div>
                  ))}
                </div>

                {/* Progress bar */}
                <div className="bg-card rounded-xl border border-border shadow-elevation-2 p-4 mb-5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-foreground">{t('overallProgress')}</span>
                    <span className={`text-sm font-bold ${completionRate >= 80 ? 'text-emerald-600' : completionRate >= 50 ? 'text-amber-600' : 'text-red-600'}`}>{completionRate}%</span>
                  </div>
                  <ProgressBar value={completionRate} color={getBarColor(completionRate)} />
                </div>

                {/* Active tasks */}
                {activeTasks?.length > 0 && (
                  <div className="bg-card rounded-xl border border-border shadow-elevation-2 overflow-hidden mb-4">
                    <div className="px-5 py-3.5 border-b border-border flex items-center justify-between">
                      <h2 className="font-semibold text-foreground">{t('activeTasks')}</h2>
                      <span className="text-xs font-bold text-muted-foreground">{activeTasks?.length}</span>
                    </div>
                    <div className="divide-y divide-border">
                      {activeTasks?.map(task => {
                        const workerObj = workers?.find(w => w?.id === task?.assigned_to_user_id);
                        const overdue = isOverdue(task);
                        return (
                          <div key={task?.id} className={`px-4 py-4 flex items-start gap-3 hover:bg-muted/20 transition-colors min-h-[64px] ${overdue ? 'border-l-4 border-l-orange-400' : task?.status === 'blocked' ? 'border-l-4 border-l-red-400' : ''}`}>
                            <button
                              onClick={() => !siteIsReadOnly && setCompletingTask(task)}
                              disabled={siteIsReadOnly}
                              className={`w-10 h-10 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all disabled:opacity-40 disabled:cursor-not-allowed ${overdue ? 'border-orange-400 hover:bg-orange-50' : task?.status === 'blocked' ? 'border-red-400' : 'border-border hover:border-accent hover:bg-accent/5'}`}
                            >
                              {task?.status === 'blocked' ? <Icon name="X" size={14} className="text-red-400" /> : <Icon name="Check" size={14} className={overdue ? 'text-orange-400' : 'text-muted-foreground'} />}
                            </button>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2">
                                <p className="text-sm font-semibold text-foreground leading-snug">{task?.title}</p>
                                <div className="flex items-center gap-1.5 flex-shrink-0">
                                  {overdue && <span className="text-xs font-bold text-orange-600 bg-orange-50 px-1.5 py-0.5 rounded">Overdue</span>}
                                  {task?.status === 'blocked' && <span className="text-xs font-bold text-red-600 bg-red-50 px-1.5 py-0.5 rounded">Blocked</span>}
                                </div>
                              </div>
                              {task?.description && (
                                <p className="text-xs text-muted-foreground mt-1 line-clamp-2 whitespace-pre-wrap">
                                  {task?.description}
                                </p>
                              )}
                              <div className="flex items-center gap-2 mt-1 flex-wrap">
                                <span className={`px-1.5 py-0.5 rounded text-xs font-medium border ${CATEGORY_COLORS?.[task?.category]}`}>{task?.category}</span>
                                {task?.required && <span className="text-xs text-red-500 font-medium">Mandatory</span>}
                                {task?.due_time && <span className="text-xs text-muted-foreground flex items-center gap-0.5"><Icon name="Clock" size={10} />{task?.due_time}</span>}
                                {!isWorker && workerObj && <span className="text-xs text-muted-foreground">{workerObj?.full_name}</span>}
                              </div>
                              {task?.notes && <p className="text-xs text-muted-foreground mt-1 italic">{task?.notes}</p>}
                            </div>
                            <div className="flex flex-col gap-1 flex-shrink-0">
                              <button
                                onClick={() => !task?._isLocal && setNotesTask(task)}
                                disabled={!!task?._isLocal}
                                className="min-w-[44px] min-h-[36px] flex items-center justify-center rounded-lg hover:bg-muted/40 transition-colors text-muted-foreground hover:text-accent disabled:opacity-30"
                                title="View notes"
                              >
                                <Icon name="MessageSquare" size={15} />
                              </button>
                              <button
                                onClick={() => !siteIsReadOnly && setCompletingTask(task)}
                                disabled={siteIsReadOnly}
                                className="min-w-[44px] min-h-[36px] flex items-center justify-center rounded-lg hover:bg-muted/40 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                aria-label="Complete task"
                              >
                                <Icon name="ChevronRight" size={16} className="text-muted-foreground" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Completed tasks */}
                {completedTasks?.length > 0 && (
                  <div className="bg-card rounded-xl border border-border shadow-elevation-2 overflow-hidden">
                    <button onClick={() => setShowCompleted(!showCompleted)} className="w-full px-5 py-3.5 flex items-center justify-between hover:bg-muted/20 transition-colors">
                      <div className="flex items-center gap-2">
                        <Icon name="CheckCircle2" size={16} className="text-emerald-500" />
                        <h2 className="font-semibold text-foreground">{t('completed')}</h2>
                        <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">{completedTasks?.length}</span>
                      </div>
                      <Icon name={showCompleted ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-muted-foreground" />
                    </button>
                    {showCompleted && (
                      <div className="divide-y divide-border border-t border-border">
                        {completedTasks?.map(task => (
                          <div key={task?.id} className="px-5 py-3.5 flex items-center gap-3 opacity-70 cursor-pointer hover:opacity-100 hover:bg-muted/20 transition-all" onClick={() => setDetailTask(task)}>
                            <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                              <Icon name="Check" size={12} className="text-emerald-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-foreground line-through">{task?.title}</p>
                              {task?.completed_at && (
                                <p className="text-xs text-emerald-600 mt-0.5">Completed by {task?.completed_by_user?.full_name || 'Unknown'} at {new Date(task?.completed_at)?.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</p>
                              )}
                            </div>
                            <Icon name="ChevronRight" size={14} className="text-muted-foreground flex-shrink-0" />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {filteredTasks?.length === 0 && (
                  <div className="bg-card rounded-xl border border-border p-12 text-center">
                    <Icon name="ClipboardList" size={32} className="text-muted-foreground mx-auto mb-3" />
                    <p className="text-sm font-medium text-foreground">{t('noTasksFound')}</p>
                    <p className="text-xs text-muted-foreground mt-1">{t('noTasksFoundDesc')}</p>
                    {!isWorker && <Button variant="primary" size="sm" className="mt-4" onClick={() => navigate('/task-planner')}>{t('planTasks')}</Button>}
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>
      {completingTask && (
        <CompleteModal
          task={completingTask}
          onClose={() => setCompletingTask(null)}
          onComplete={handleComplete}
          requirePhotoEvidence={isWorker ? requirePhotoEvidence : false}
        />
      )}
      {notesTask && (
        <TaskNotesModal
          task={notesTask}
          companyId={companyId}
          onClose={() => setNotesTask(null)}
        />
      )}
      {detailTask && (
        <TaskDetailModal task={detailTask} onClose={() => setDetailTask(null)} />
      )}
    </>
  );
};

export default TaskCompletionWorkflow;
